import Color from 'color';
import { calculateAccessibleText } from './colorSchemeUtils';
import { ColorScheme } from '../schema/ColorScheme';
import { getDefaultOutlookColorScheme, isDefaultOutlookColor } from './defaultOutlookColorScheme';
import { getIsDarkTheme } from 'mini-theme';
import { getPrimaryColorName } from './defaultPrimaryColors';
import { shadeColor } from './colorOperations';

const shadeValues = {
    schemeDarker: -44,
    schemeDark: -24,
    schemeDarkAlt: -10,
    schemePrimary: 0,
    schemeSecondary: 12,
    schemeTertiary: 40,
    schemeLight: 70,
    schemeLighter: 84,
    schemeLighterAlt: 96,
};

/**
 * Cache of color schemes for a given primary color and light/dark mode combination
 */
const colorSchemeCache: Record<string, ColorScheme> = {};

const createCacheKey = (hexColor: string, isDarkTheme: boolean) =>
    `${hexColor}_${isDarkTheme ? 'dark' : 'light'}`;

/**
 * Get the color scheme for a given primary color scheme, with caching.
 * @param primaryHexColor the primary color for the scheme.
 */
export function getColorSchemeFromPrimaryColor(
    primaryHexColor: string,
    isDarkTheme: boolean = getIsDarkTheme()
): ColorScheme {
    const cacheKey = createCacheKey(primaryHexColor, isDarkTheme);
    let colorScheme = colorSchemeCache[cacheKey];
    if (!colorScheme) {
        colorScheme = generateColorSchemeFromPrimaryColor(primaryHexColor, isDarkTheme);
        colorSchemeCache[cacheKey] = colorScheme;
    }
    return colorScheme;
}

/**
 * We dynamically generate a color scheme given a primary color value by taking the primary color and shading it with a % of black and % of white
 *
 * The percentages of white and black come from shadeValues, and create a ColorScheme where the given primary color corresponds to schemePrimary, with each darker
 * shade mixing extra black and each lighter shade mixing extra white.
 *
 * Code from universal color generation is here:
 * https://msasg.visualstudio.com/_search?type=code&lp=custom-Collection&text=CalendarColorShadeGenerator&result=DefaultCollection%2FOfficeSourceDepot%2Fdevmain%2Fdevmain%2Fhxcalendarui%2Fxaml%2FSupport%2FcolorShadeGenerator%2FCalendarColorShadeGenerator.cpp&preview=1&_a=search
 *
 * @param primaryHexColor the string value of the color to use as primary
 */
function generateColorSchemeFromPrimaryColor(
    primaryHexColor: string,
    isDarkTheme: boolean
): ColorScheme {
    const primaryColor = Color(primaryHexColor);
    if (isDefaultOutlookColor(primaryHexColor)) {
        return getDefaultOutlookColorScheme(isDarkTheme);
    }
    const colorScheme: ColorScheme = {
        text: '',
        textAlt: '',
        accessibleText: {
            schemePrimaryBackground: '',
            schemeLighterBackground: '',
            msColorWhiteBackground: '',
            msColorNeutralLighterBackground: '',
        },
        schemeDarker: shadeColor(
            primaryColor,
            isDarkTheme ? shadeValues.schemeLighterAlt : shadeValues.schemeDarker
        ).hex(),
        schemeDark: shadeColor(
            primaryColor,
            isDarkTheme ? shadeValues.schemeLighter : shadeValues.schemeDark
        ).hex(),
        schemeDarkAlt: shadeColor(
            primaryColor,
            isDarkTheme ? shadeValues.schemeLight : shadeValues.schemeDarkAlt
        ).hex(),
        schemePrimary: primaryColor.hex(),
        schemeSecondary: shadeColor(
            primaryColor,
            isDarkTheme ? -shadeValues.schemeSecondary : shadeValues.schemeSecondary
        ).hex(),
        schemeTertiary: shadeColor(
            primaryColor,
            isDarkTheme ? -shadeValues.schemeTertiary : shadeValues.schemeTertiary
        ).hex(),
        schemeLight: shadeColor(
            primaryColor,
            isDarkTheme ? shadeValues.schemeDarkAlt : shadeValues.schemeLight
        ).hex(),
        schemeLighter: shadeColor(
            primaryColor,
            isDarkTheme ? shadeValues.schemeDark : shadeValues.schemeLighter
        ).hex(),
        schemeLighterAlt: shadeColor(
            primaryColor,
            isDarkTheme ? shadeValues.schemeDarker : shadeValues.schemeLighterAlt
        ).hex(),
        colorName: () => getPrimaryColorName(primaryColor.hex()),
    };

    colorScheme.accessibleText = calculateAccessibleText(colorScheme);
    colorScheme.text = colorScheme.accessibleText.schemePrimaryBackground;
    colorScheme.textAlt = colorScheme.accessibleText.schemeLighterBackground;

    return colorScheme;
}
